import React, {useEffect, useState} from 'react';
import EditIcon from '@mui/icons-material/Edit';
import {Button, Box, Card, CircularProgress, Container, Grid, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, TextField, IconButton, Dialog, Tooltip} from '@mui/material';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import TableSortLabel from '@mui/material/TableSortLabel';
import SearchIcon from '@mui/icons-material/Search';
import {getFilteredClasses} from '../components/SearchBar';
import {EditClass} from './EditClass';

interface ClassItem {
    courseTitle: string;
    endDate: string;
    uploadFile: string;
    classId: string;
    startDate: string;
    status: string;
    instructor: string;
    location: string;
    classStartDate: string;
    classEndDate: string;
    hasClassStarted?: string;
}
interface RequestClassProps {
    setRequestClass: any;
    userType: any;
    cdiCourses: any;
    submitForm: any;
}
const DEFAULT_ROWS_PER_PAGE = 25;
const ROWS_PER_PAGE_25 = 25;
const ROWS_PER_PAGE_50 = 50;
const ROWS_PER_PAGE_100 = 100;
const ROWS_PER_PAGE_OPTIONS = [ROWS_PER_PAGE_25, ROWS_PER_PAGE_50, ROWS_PER_PAGE_100];

const compareDates = (a: string, b: string, order: 'asc' | 'desc'): number => {
    const dateA = new Date(a);
    const dateB = new Date(b);

    return order === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
};

const compareStrings = (a: string, b: string, order: 'asc' | 'desc'): number => {
    return order === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
};

const compare = (a: string, b: string, order: 'asc' | 'desc', isDate: boolean = false): number => {
    return isDate ? compareDates(a, b, order) : compareStrings(a, b, order);
};

const getSortedClasses = (classes: ClassItem[], order: 'asc' | 'desc', orderBy: keyof ClassItem): ClassItem[] => {
    const isDate = ['startDate', 'endDate', 'classStartDate', 'classEndDate'].includes(orderBy);

    return classes.sort((a, b) => compare(a[orderBy] ?? '', b[orderBy] ?? '', order, isDate));
};

const getDirection = (orderBy: keyof ClassItem, currentOrderBy: keyof ClassItem, order: 'asc' | 'desc'): 'asc' | 'desc' => {
    return orderBy === currentOrderBy ? order : 'asc';
};

const SUNDAY = 0;
const SATURDAY = 6;
const BUSINESS_DAYS_REQUIRED = 2;

const isWeekday = (dayOfWeek: number): boolean => {
    return dayOfWeek !== SUNDAY && dayOfWeek !== SATURDAY;
};

const calculateBusinessDays = (startDate: string, endDate: string): number => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    let count = 0;
    const current = new Date(start);

    while (current < end) {
        if (isWeekday(current.getDay())) {
            count += 1;
        }
        current.setDate(current.getDate() + 1);
    }

    return count;
};

// eslint-disable-next-line complexity, max-statements
const isEditEnabled = (submissionDate: string, classStartDate: string, classId: string): boolean => {
    console.log('Class ID before check:', classId);

    const now = new Date();
    const nowStr = now.toLocaleDateString('en-US');
    const classStart = new Date(classStartDate);

    // Check if classId is blank, null, or "-"
    if (!classId || classId.trim() === '' || classId.trim() === '-') {
        return false;
    }

    // Check if class start date is in the past
    if (classStart < now) {
        return false;
    }

    // Check if at least 2 business days (48 hours) have passed since submission
    const daysSinceSubmission = calculateBusinessDays(submissionDate, nowStr);

    // Check if there are at least 2 business days (48 hours) remaining before class starts
    const daysUntilClassStart = calculateBusinessDays(nowStr, classStartDate);

    // Enable edit only if both conditions are met (at least 2 business days = 48 hours)
    return daysSinceSubmission >= BUSINESS_DAYS_REQUIRED && daysUntilClassStart >= BUSINESS_DAYS_REQUIRED;
};

interface HandlePopoverOpenParams {
    event: React.MouseEvent<HTMLButtonElement>;
    classId: string;
    courseTitle: string;
    instructor: any;
}

const handlePopoverOpen = (params: HandlePopoverOpenParams, setTableState: any): void => {
    const {
        event,
        classId,
        courseTitle,
        instructor
    } = params;

    setTableState((prev: any) => ({
        ...prev,
        anchorEl: event.target,
        popoverClassId: classId,
        isDialogOpen: true, // Open the dialog when edit is clicked
        courseTitle,
        instructor
    }));
};

const handleDialogClose = (state: any, setState: any): void => {
    setState((prev: any) => ({
        ...prev,
        isDialogOpen: false,
        anchorEl: null,
        popoverClassId: null
    }));
};

const findDetailedCourse = (cdiCourses: any, courseTitle: string): any => {
    return cdiCourses?.find((course: any) => `${course.courseId} : ${course.courseTitle}` === courseTitle) || {};
};

interface RenderEditClassParams {
    tableState: any;
    setTableState: any;
    userType: any;
    cdiCourses: any;
    submitForm: any;
}

const renderEditClass = (params: RenderEditClassParams): any => {
    const {
        tableState,
        setTableState,
        userType,
        cdiCourses,
        submitForm
    } = params;
    const detailedCourseTitle = findDetailedCourse(cdiCourses, tableState.courseTitle);
    const modifiedSubmitForm = (reqState: any, courseDetails: any): any => {
        const enhancedCourseDetails = {
            ...courseDetails,
            'classId': tableState.popoverClassId || ''
        };

        return submitForm(reqState, enhancedCourseDetails);
    };

    return (
        <EditClass
            DialogOpen={tableState.isDialogOpen}
            cdiCourses={[detailedCourseTitle]}
            classId={tableState.popoverClassId || ''}
            handleDialogClose={(): any => handleDialogClose(tableState, setTableState)}
            instructor={tableState.instructor || ''}
            submitForm={modifiedSubmitForm}
            userType={userType}
        />
    );
};

const handleChangePage = (_event: unknown, newPage: number, setTableState: any): void => {
    setTableState((prevState: any) => ({
        ...prevState,
        page: newPage
    }));
};

const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, setTableState: any): void => {
    setTableState((prevState: any) => ({
        ...prevState,
        rowsPerPage: parseInt(event.target.value, 10),
        page: 0
    }));
};

const handleRequestSort = (property: keyof ClassItem, tableState: any, setTableState: any): void => {
    const isAsc = tableState.orderBy === property && tableState.order === 'asc';

    setTableState((prevState: any) => ({
        ...prevState,
        order: isAsc ? 'desc' : 'asc',
        orderBy: property
    }));
};

const formatClassData = (data: any): ClassItem[] => {
    if (!Array.isArray(data.message)) {
        return [];
    }

    return data.message.map((item: any) => ({
        courseTitle: item.course_title,
        endDate: item.end_date,
        uploadFile: item.upload_file,
        classId: item.class_id,
        startDate: item.start_date,
        status: item.status,
        instructor: item.instructor,
        location: item.location,
        classStartDate: item.class_start_date,
        classEndDate: item.class_end_date
    }));
};

const fetchPastClasses = (accessToken: string | undefined, setClasses: any, setLoading: any): void => {
    setLoading(true);
    void fetch(`${process.env.REACT_APP_BACKEND_URL}/pastclassrequests`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json; charset=UTF-8',
            'Accept': 'application/json'
        }
    })
        .then((res) => res.json())
        .then((data) => {
            setClasses(formatClassData(data));
            return data;
        })
        .catch((error) => {
            console.error('Error fetching classes:', error);
        })
        .finally(() => {
            setLoading(false);
        });
};

const getNoResultsText = (searchTerm: string, t: any): string =>
    searchTerm === '' ? t('requestClassHistory.noRequests') : t('requestClassHistory.noResults');

const getEditTooltipText = (classItem: ClassItem, t: any): string =>
    isEditEnabled(classItem.startDate, classItem.classStartDate, classItem.classId) ? t('requestClassHistory.editTooltip') : t('requestClassHistory.editDisabledTooltip');

interface TableSectionProps {
    loading: boolean;
    sortedClasses: ClassItem[];
    tableState: any;
    setTableState: any;
    t: any;
}

const renderTableSection = ({
    loading, sortedClasses, tableState, setTableState, t
}: TableSectionProps): JSX.Element => {
    if (loading) {
        return (
            <Card
                sx={{
                    margin: '1rem 0',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '60vh'
                }}
            >
                <Box
                    alignItems='center'
                    display='flex'
                    flexDirection='column'
                    gap={2}
                >
                    <CircularProgress/>
                    <Typography>{t('common.loading')}</Typography>
                </Box>
            </Card>
        );
    }
    if (sortedClasses.length > 0) {
        return (
            <Card
                sx={{
                    marginTop: '1rem',
                    width: '100%'
                }}
            >
                <TableContainer>
                    <Table
                        sx={{
                            width: '100%',
                            fontSize: {
                                xs: '0.8rem',
                                sm: '1rem'
                            },
                            tableLayout: 'auto'
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontSize: {
                                            xs: '0.7rem', // Small screens
                                            sm: '0.9rem' // Medium screens
                                        }
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'classId'}
                                        direction={getDirection('classId', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('classId', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.classId')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'courseTitle'}
                                        direction={getDirection('courseTitle', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('courseTitle', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.courseTitle')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'instructor'}
                                        direction={getDirection('instructor', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('instructor', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.instructor')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'location'}
                                        direction={getDirection('location', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('location', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.location')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'startDate'}
                                        direction={getDirection('startDate', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('startDate', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.submissionDate')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'classStartDate'}
                                        direction={getDirection('classStartDate', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('classStartDate', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.classStartDate')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    <TableSortLabel
                                        active={tableState.orderBy === 'classEndDate'}
                                        direction={getDirection('classEndDate', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('classEndDate', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.classEndDate')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={tableState.orderBy === 'status'}
                                        direction={getDirection('status', tableState.orderBy, tableState.order)}
                                        onClick={(): void => handleRequestSort('status', tableState, setTableState)}
                                    >
                                        {t('requestClassHistory.status')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        textAlign: 'center',
                                        fontSize: {
                                            xs: '0.7rem',
                                            sm: '0.9rem'
                                        }
                                    }}
                                >
                                    {t('requestClassHistory.action')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedClasses.slice(tableState.page * tableState.rowsPerPage, tableState.page * tableState.rowsPerPage + tableState.rowsPerPage).map((classItem, index) => (
                                <TableRow key={index}>
                                    <TableCell>{classItem.classId}</TableCell>
                                    <TableCell>{classItem.courseTitle}</TableCell>
                                    <TableCell>{classItem.instructor}</TableCell>
                                    <TableCell>{classItem.location}</TableCell>
                                    <TableCell>{classItem.startDate}</TableCell>
                                    <TableCell>{classItem.classStartDate}</TableCell>
                                    <TableCell>{classItem.classEndDate}</TableCell>
                                    <TableCell>{classItem.status}</TableCell>
                                    <TableCell
                                        sx={{
                                            width: 100,
                                            textAlign: 'center',
                                            padding: '4px'
                                        }}
                                    >
                                        <Tooltip
                                            title={getEditTooltipText(classItem, t)}
                                        >
                                            <span>
                                                <IconButton
                                                    aria-describedby={`popover-${classItem.classId}`}
                                                    aria-label='edit'
                                                    disabled={!isEditEnabled(classItem.startDate, classItem.classStartDate, classItem.classId)}
                                                    id={`popover-button-id-${classItem.classId}`}
                                                    onClick={(e): void =>
                                                        handlePopoverOpen({
                                                            event: e,
                                                            classId: classItem.classId,
                                                            courseTitle: classItem.courseTitle,
                                                            instructor: classItem.instructor
                                                        }, setTableState)
                                                    }
                                                    size='small'
                                                >
                                                    <EditIcon fontSize='small'/>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component='div'
                    count={sortedClasses.length}
                    onPageChange={(e, page): void => handleChangePage(e, page, setTableState)}
                    onRowsPerPageChange={(e): void => handleChangeRowsPerPage(e, setTableState)}
                    page={tableState.page}
                    rowsPerPage={tableState.rowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    sx={{
                        fontSize: {
                            xs: '0.8rem', // Small screens
                            sm: '1rem' // Medium screens
                        }
                    }}
                />
            </Card>
        );
    }
    return (
        <Card
            sx={{
                margin: '1rem 0',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh'
            }}
        >
            <Typography
                align={'center'}
                component='p'
                sx={{
                    fontSize: {
                        xs: '1rem', // Small screens
                        sm: '1.5rem' // Medium screens
                    }
                }}
                variant='h4'
            >
                {getNoResultsText(tableState.searchTerm, t)}
            </Typography>
        </Card>
    );
};

const ClassList: React.FC<RequestClassProps> = ({
    setRequestClass, userType, cdiCourses, submitForm
}): JSX.Element => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [classes, setClasses] = useState<ClassItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [tableState, setTableState] = useState({
        classes: [] as ClassItem[],
        page: 0,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        order: 'desc' as 'asc' | 'desc',
        orderBy: 'startDate' as keyof ClassItem,
        searchTerm: '',
        anchorEl: null as HTMLElement | null,
        popoverClassId: null as string | null,
        isDialogOpen: false,
        activeAlert: false,
        alertMessage: '',
        alertType: 'success' as 'success' | 'error'
    });

    useEffect(() => {
        fetchPastClasses(authState?.accessToken?.accessToken, setClasses, setLoading);
    }, [authState]);

    const sortedClasses = getSortedClasses(getFilteredClasses(classes, tableState.searchTerm), tableState.order, tableState.orderBy);

    return (
        <Container
            maxWidth={false}
            sx={{
                paddingTop: '1rem',
                textAlign: 'center',
                paddingX: '2rem'
            }}
        >
            <Grid
                alignItems='center'
                container
                justifyContent='space-between'
            >
                <Grid item>
                    <Typography variant='h4'>{t('requestClassHistory.title')}</Typography>
                </Grid>
            </Grid>
            <Grid
                container
                justifyContent='space-between'
                sx={{
                    paddingTop: '1rem'
                }}
            >
                <Grid item>
                    <TextField
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon/>
                                </InputAdornment>
                            )
                        }}
                        onChange={(e): any => setTableState((prevState) => ({
                            ...prevState,
                            searchTerm: e.target.value,
                            page: 0
                        }))}
                        placeholder={t('common.search')}
                        sx={{
                            width: '100%',
                            maxWidth: '300px',
                            textAlign: 'left',
                            fontSize: {
                                xs: '0.8rem', // Small screens
                                sm: '1rem' // Medium screens
                            }
                        }}
                        value={tableState.searchTerm}
                        variant='outlined'
                    />
                </Grid>
                <Grid item>
                    <Button
                        onClick={(): any => setRequestClass((prev: any) => (
                            {
                                ...prev,
                                requestClass: true
                            }))}
                        variant='primary'
                    >
                        {t('requestClassHistory.requestClass')}
                    </Button>
                </Grid>
            </Grid>
            {renderTableSection({
                loading,
                sortedClasses,
                tableState,
                setTableState,
                t
            })}
            <Dialog
                // fullWidth
                maxWidth='lg'
                onClose={(): any => handleDialogClose(tableState, setTableState)}
                open={tableState.isDialogOpen}
            >
                {renderEditClass({
                    tableState,
                    setTableState,
                    userType,
                    cdiCourses,
                    submitForm
                })}
            </Dialog>
        </Container>
    );
};

export default ClassList;
